import { useGoogleLogin } from '@react-oauth/google';
import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { LoginResponse } from './useLoginMutation';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';

const SocialAuthMutationGQL = gql`
  mutation socialLogin($input: SocialLoginInput!) {
    socialLogin(input: $input) {
      token, uri, sid, verified, email, id
    }
  }
`;

export default function useGoogleAuth(type: 'Login' | 'Registration') {
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false)
  const { setAuthToken, removeAuthToken } = useAuthToken()
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();
  const [googleAuthUser, setGoogleUser] = useState<{ email: string; given_name: string; family_name: string }>()

  const { identify, trackEvent, segment } = useAnalytics();

  const [mutation, { error }] = useMutation<{ socialLogin: LoginResponse }>(SocialAuthMutationGQL, {
    onCompleted: ({ socialLogin }) => {
      if (socialLogin.token) {
        removeAuthToken();
        setAuthToken(socialLogin.token, true);
        if (socialLogin.id) {
          segment?.identify(socialLogin.id);
          identify(socialLogin.id, {
            email: socialLogin.email,
          });
        }
        const d = { email: socialLogin.email, loginType: 'Google', userId: socialLogin?.id };
        trackEvent(Events.actions.logIn, d);
        if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {          
          const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
          push(sharePath);  
        } else {
          push('/');
        }
      } else if (socialLogin.sid) {
        const state: any = { totpId: socialLogin.sid, email: socialLogin.email }
        if (socialLogin.verified !== 'true' && socialLogin.uri) {
          state.totpUri = socialLogin.uri
        }
        push('/verify', state);
      } else if (socialLogin.email) {
        push('/admin/verify', { email: socialLogin.email });
      }
    },
    onError: (error, options) => {
      trackEvent(
        Events.actions.logInFailed,
        {
          email: options?.variables?.email,
          loginType: 'Google',
          clientId: options?.variables?.clientId,
          message: "Login attempt failed",
          error: error.message,
        }
      );
      logger.debug(`[Error]`, error);
    }
  })

  const googleAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleAuthLoading(true)
      try {
        if (type === 'Login') {
          await mutation({
            variables: {
              input: {
                accessToken: tokenResponse.access_token,
                clientId: localStorage.getItem('clientId'),
                provider: 'Google'
              }
            }
          })
        } else {
          const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            method: 'GET',
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          });
          const userInfo = await response.json();
          if (userInfo.email) {
            setGoogleUser({
              email: userInfo.email,
              family_name: userInfo.family_name ?? '',
              given_name: userInfo.given_name ?? ''
            })
          }
        }
      } catch (error) {
        logger.error(error);
      } finally {
        setGoogleAuthLoading(false)
      }
    },
    onError: errorResponse => logger.info(errorResponse),
  });

  return {
    googleAuth,
    googleAuthUser,
    googleAuthLoading,
    googleAuthError: error,
    setGoogleUser
  }
}
